/**
 * `IndexerLagCards` — pair of summary cards rendered directly under the
 * "All systems operational" banner. Each card surfaces the current indexer
 * lag (in seconds) for one API surface, anchored on `getMasterchainInfo`:
 *
 *   - V3 → `GET /api/v3/masterchainInfo (freshness)`
 *   - V2 → first V2_KEYS entry with a live reading (canonical:
 *           `GET /api/v2/getMasterchainInfo (freshness)`; legacy
 *           `getConsensusBlock` covers windows straddling a probe swap —
 *           see V2_KEYS below)
 */
import { useQueries } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { isFreshnessEndpoint } from '../lib/endpoint-key';
import { lagToTone } from '../lib/lag-bars';
import type { LagWindow } from '../lib/lag-time';
import type { ComponentGroup, ComponentSummary } from '../lib/filter-network';
import { uptimeQueryKey } from '../lib/uptime-api';
import {
  lagViewParam,
  lagWindowParam,
  parseUrlState,
  patchUrlParams,
  type LagView,
} from '../lib/url-state';
import { formatLag } from './IndexerPipelineCard';
import { LagDetailModal } from './LagDetailModal';
import { LagHistoryBar, type LagBucketView } from './LagHistoryBar';

const V3_KEY = 'GET /api/v3/masterchainInfo (freshness)';
// The V2 suite has swapped its single freshness probe twice
// (getConsensusBlock → getMasterchainInfo on 2026-05-23, silently back on
// 2026-05-31, restored 2026-07-22 — see the autotests suites'
// probes/v2/freshness.go). Rows of the retired key linger inside the window
// after each swap, so `pickFreshnessEndpoint` prefers the first key with a
// LIVE reading (non-null currentLagSec) and uses bare presence only as a
// fallback — hard-pinning an order froze the card on a dead endpoint's
// stale reading after both cutovers. Order below is the current canonical
// probe first (chained getMasterchainInfo → getBlockHeader).
const V2_KEYS: readonly string[] = [
  'GET /api/v2/getMasterchainInfo (freshness)',
  'GET /api/v2/getConsensusBlock (freshness)',
];

type ApiSurface = 'v2' | 'v3';

interface EndpointLag {
  endpointKey: string;
  currentLagSec?: number | null;
  p95LagSec?: number | null;
  maxLagSec?: number | null;
  lagBuckets?: readonly LagBucketView[];
}

interface UptimeResponse {
  bucketMs?: number;
  endpoints: EndpointLag[];
}

function pickComponent(
  groups: readonly ComponentGroup[],
  network: 'mainnet' | 'testnet',
  surface: ApiSurface,
): ComponentSummary | null {
  const matching: ComponentSummary[] = [];
  for (const group of groups) {
    for (const component of group.components) {
      if (component.network !== network) continue;
      const suite = component.key.split('::')[1] ?? '';
      if (surface === 'v3' && suite.startsWith('liveness-v3-rest')) {
        matching.push(component);
      } else if (surface === 'v2' && suite.startsWith('liveness-v2-rest')) {
        matching.push(component);
      }
    }
  }
  if (matching.length === 0) return null;
  const goSuite = matching.find((c) =>
    (c.key.split('::')[1] ?? '').endsWith('-go'),
  );
  return goSuite ?? matching[0];
}

function pickFreshnessEndpoint(
  endpoints: readonly EndpointLag[],
  surface: ApiSurface,
): EndpointLag | null {
  if (surface === 'v3') {
    return endpoints.find((ep) => ep.endpointKey === V3_KEY) ?? null;
  }
  // Liveness-first: a retired probe's rows linger in the window after a
  // suite recomposition, so bare presence is not enough — anchor on the
  // first key that carries a live reading, then fall back to presence.
  for (const key of V2_KEYS) {
    const hit = endpoints.find((ep) => ep.endpointKey === key);
    if (hit && hit.currentLagSec !== null && hit.currentLagSec !== undefined) {
      return hit;
    }
  }
  for (const key of V2_KEYS) {
    const hit = endpoints.find((ep) => ep.endpointKey === key);
    if (hit) return hit;
  }
  return (
    endpoints.find(
      (ep) =>
        isFreshnessEndpoint(ep.endpointKey) &&
        ep.endpointKey.includes('/api/v2/') &&
        (ep.endpointKey.includes('Masterchain') ||
          ep.endpointKey.includes('ConsensusBlock')),
    ) ?? null
  );
}

async function fetchUptime1h(id: number): Promise<UptimeResponse> {
  const response = await fetch(
    `/api/v1/components/${String(id)}/uptime?window=1h`,
    { headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw new Error(
      `uptime fetch failed for component ${String(id)}: ${String(response.status)}`,
    );
  }
  return (await response.json()) as UptimeResponse;
}

export interface IndexerLagCardsProps {
  groups: readonly ComponentGroup[];
  network: 'mainnet' | 'testnet';
}

interface LagDetail {
  surface: ApiSurface;
  componentId: number;
  endpointKey: string;
  title: string;
  view: LagView;
  window: LagWindow;
}

interface PendingLagLink {
  surface: ApiSurface;
  view: LagView;
  window: LagWindow;
}

/**
 * `?lag=v2|v3` deep link, read from the CURRENT URL at mount — not from a
 * page-load snapshot. This component unmounts while an incident detail is
 * open; on remount a snapshot would replay a deep link the user already
 * consumed and dismissed (closing the modal strips the params), while the
 * live URL always reflects whether the link is still pending.
 */
function parseLagDeepLink(): PendingLagLink | null {
  if (typeof window === 'undefined') return null;
  const state = parseUrlState(window.location.search);
  if (state.lag === null) return null;
  return { surface: state.lag, view: state.lagView, window: state.lagWindow };
}

function IndexerLagCardsImpl({ groups, network }: IndexerLagCardsProps) {
  const v3Component = useMemo(
    () => pickComponent(groups, network, 'v3'),
    [groups, network],
  );
  const v2Component = useMemo(
    () => pickComponent(groups, network, 'v2'),
    [groups, network],
  );

  // Cache keys align with ServiceCard's `['uptime', id, '1h', '']` (App.tsx)
  // so React Query dedupes — no double network calls when ServiceCard
  // expands later.
  const queries = useQueries({
    queries: [v3Component, v2Component].map((component) => ({
      queryKey: uptimeQueryKey(component?.id ?? -1, '1h', ''),
      queryFn: () =>
        component
          ? fetchUptime1h(component.id)
          : Promise.reject(new Error('no component')),
      enabled: component !== null,
      refetchInterval: 30_000,
      staleTime: 15_000,
    })),
  });

  const v3Endpoint = useMemo(
    () =>
      queries[0].data
        ? pickFreshnessEndpoint(queries[0].data.endpoints, 'v3')
        : null,
    [queries[0].data],
  );
  const v2Endpoint = useMemo(
    () =>
      queries[1].data
        ? pickFreshnessEndpoint(queries[1].data.endpoints, 'v2')
        : null,
    [queries[1].data],
  );

  const [detail, setDetail] = useState<LagDetail | null>(null);
  // One-shot deep link: the surface's component + freshness endpoint arrive
  // asynchronously, so the request parks here until they resolve, then opens
  // the modal with the linked view/window. Any explicit user open/close (and
  // a network switch) cancels it — late-resolving data must never hijack a
  // modal the user opened, nor resurrect one they dismissed.
  const [pending, setPending] = useState<PendingLagLink | null>(
    parseLagDeepLink,
  );

  const openDetail = useCallback(
    (surface: ApiSurface, view: LagView, windowSel: LagWindow) => {
      const component = surface === 'v3' ? v3Component : v2Component;
      const endpoint = surface === 'v3' ? v3Endpoint : v2Endpoint;
      if (!component || !endpoint) return;
      setPending(null);
      setDetail({
        surface,
        componentId: component.id,
        endpointKey: endpoint.endpointKey,
        title: surface === 'v3' ? 'API v3 indexer lag' : 'API v2 indexer lag',
        view,
        window: windowSel,
      });
      patchUrlParams({
        lag: surface,
        lagView: lagViewParam(view),
        lagWindow: lagWindowParam(windowSel),
      });
    },
    [v2Component, v3Component, v2Endpoint, v3Endpoint],
  );

  const closeDetail = useCallback(() => {
    setPending(null);
    setDetail(null);
    patchUrlParams({ lag: null, lagView: null, lagWindow: null });
  }, []);

  useEffect(() => {
    if (pending === null) return;
    const component = pending.surface === 'v3' ? v3Component : v2Component;
    const endpoint = pending.surface === 'v3' ? v3Endpoint : v2Endpoint;
    if (!component || !endpoint) return;
    setPending(null);
    // If the user beat the deep link to it and some modal is already open
    // (only ModalShell renders role="dialog"), cancel instead of stacking a
    // second shell — duplicate layoutIds and doubled Escape handlers.
    if (document.querySelector('[role="dialog"]') !== null) return;
    openDetail(pending.surface, pending.view, pending.window);
  }, [pending, openDetail, v2Component, v3Component, v2Endpoint, v3Endpoint]);

  // Drop the open detail (and any unconsumed deep link) when the operator
  // switches network — the modal's component id would otherwise point at the
  // previous network's suite. Ref-guarded so the mount-time run doesn't kill
  // a deep link before its data has a chance to load.
  const prevNetwork = useRef(network);
  useEffect(() => {
    if (prevNetwork.current === network) return;
    prevNetwork.current = network;
    closeDetail();
  }, [network, closeDetail]);

  if (v3Component === null && v2Component === null) return null;

  const v3Open =
    v3Component && v3Endpoint
      ? () => {
          openDetail('v3', 'bar', '1h');
        }
      : undefined;
  const v2Open =
    v2Component && v2Endpoint
      ? () => {
          openDetail('v2', 'bar', '1h');
        }
      : undefined;

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-2.5 sm:mb-9 sm:grid-cols-2">
        <LagCard
          title="API v3 indexer lag"
          endpoint={v3Endpoint}
          loading={queries[0].isLoading}
          absent={v3Component === null}
          bucketMs={queries[0].data?.bucketMs ?? 60_000}
          onOpen={v3Open}
        />
        <LagCard
          title="API v2 indexer lag"
          endpoint={v2Endpoint}
          loading={queries[1].isLoading}
          absent={v2Component === null}
          bucketMs={queries[1].data?.bucketMs ?? 60_000}
          onOpen={v2Open}
        />
      </div>
      {detail && (
        <LagDetailModal
          key={detail.surface}
          componentId={detail.componentId}
          endpointKey={detail.endpointKey}
          title={detail.title}
          network={network}
          pipeline="masterchain"
          defaultWindow={detail.window}
          defaultView={detail.view}
          onViewChange={(view) => {
            patchUrlParams({ lagView: lagViewParam(view) });
          }}
          onWindowChange={(windowSel) => {
            patchUrlParams({ lagWindow: lagWindowParam(windowSel) });
          }}
          onClose={closeDetail}
        />
      )}
    </>
  );
}

export const IndexerLagCards = memo(IndexerLagCardsImpl);

type Tone = 'ok' | 'warn' | 'down' | 'none';

const TONE_DOT_CLASSES: Record<Tone, string> = {
  ok: 'bg-ok shadow-[0_0_0_3px_rgba(26,166,107,.18)]',
  warn: 'bg-warn shadow-[0_0_0_3px_rgba(224,160,25,.2)]',
  down: 'bg-down shadow-[0_0_0_3px_rgba(227,80,62,.2)]',
  none: 'bg-line-strong',
};

interface LagCardProps {
  title: string;
  endpoint: EndpointLag | null;
  loading: boolean;
  absent: boolean;
  bucketMs: number;
  /** When set (and the card has data), the card opens the lag detail modal. */
  onOpen?: () => void;
}

function LagCard({
  title,
  endpoint,
  loading,
  absent,
  bucketMs,
  onOpen,
}: LagCardProps) {
  const tone: Tone = endpoint
    ? lagToTone(endpoint.currentLagSec ?? null, 'masterchain')
    : 'none';
  const showSkeleton = loading && !endpoint;
  const buckets = endpoint?.lagBuckets ?? null;
  const expectedCount = Math.max(1, Math.round((60 * 60_000) / bucketMs));
  const clickable = onOpen !== undefined && !!endpoint && !absent;
  const interactive = clickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': `Open ${title} detail`,
        onClick: onOpen,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen?.();
          }
        },
      }
    : {};

  return (
    <div
      {...interactive}
      className={clsx(
        'flex flex-col gap-3 rounded-xl border border-line bg-paper-1 px-4 py-3.5 shadow-card sm:px-5 sm:py-4',
        'outline-none transition-[border-color] duration-150',
        clickable &&
          'cursor-pointer hover:border-line-strong focus-visible:shadow-ring-blue',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className={clsx('h-2.5 w-2.5 rounded-full', TONE_DOT_CLASSES[tone])}
          />
          <span className="truncate text-[13px] font-semibold tracking-[-.005em] text-ink-1 sm:text-[13.5px]">
            {title}
          </span>
        </div>
        <span className="whitespace-nowrap text-[11px] font-medium text-ink-3 [font-feature-settings:'tnum'] sm:text-[11.5px]">
          last 1 hour
        </span>
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="min-w-0">
          <div
            className={clsx(
              "font-mono text-[22px] font-semibold leading-none tracking-[-.01em] [font-feature-settings:'tnum'] sm:text-[24px]",
              tone === 'ok' && 'text-ink-1',
              tone === 'warn' && 'text-warn-fg',
              tone === 'down' && 'text-down-fg',
              tone === 'none' && 'text-ink-3',
            )}
          >
            {showSkeleton ? (
              <span className="skeleton-shine inline-block h-[18px] w-12 rounded align-middle" />
            ) : absent ? (
              '—'
            ) : (
              formatLag(endpoint?.currentLagSec ?? null)
            )}
          </div>
          <div className="mt-1 truncate font-mono text-[11.5px] text-ink-3 [font-feature-settings:'tnum']">
            {absent
              ? 'no probe configured'
              : showSkeleton
                ? ' '
                : `p95 ${formatLag(endpoint?.p95LagSec ?? null)} · max ${formatLag(endpoint?.maxLagSec ?? null)}`}
          </div>
        </div>
        {buckets && buckets.length > 0 && (
          // Full-width sparkline on its own row. The bar renders a fixed 60+
          // cells at `min-w-[2px]` (~242px hard floor); a side-by-side
          // ~55–60% column can never hold that, so it overflowed the card on
          // every width (worst on mobile). Stacking gives it the full card
          // width and matches the IndexerPipelineCard freshness-row layout.
          <LagHistoryBar
            buckets={buckets}
            pipeline="masterchain"
            windowSel="1h"
            expectedCount={expectedCount}
            endpointKey={endpoint?.endpointKey ?? 'masterchain'}
          />
        )}
      </div>
    </div>
  );
}
